import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lightbulb, Sparkles, Target, Zap, ArrowRight, X } from "lucide-react";
import { FRUITS } from "@/hooks/useGameStateV2";

// Hint types with different levels of assistance
const HINT_TYPES = {
  BASIC: 'basic',           // Just highlights one valid move
  TARGETED: 'targeted',     // Shows move that helps with target fruit
  SPECIAL: 'special',       // Shows move that creates special fruit
  COMBO: 'combo',           // Shows potential combo moves
};

// Hook for advanced hint system
export function useAdvancedHint(board, targetFruit, boardSize) {
  const [hintCells, setHintCells] = useState([]);
  const [hintType, setHintType] = useState(null);
  const [hintMessage, setHintMessage] = useState(null);
  const [autoHintEnabled, setAutoHintEnabled] = useState(() => {
    const saved = localStorage.getItem('bananaBlast_autoHint');
    return saved === 'true';
  });
  const [idleTime, setIdleTime] = useState(0);

  // Clear hints
  const clearHint = useCallback(() => {
    setHintCells([]);
    setHintType(null);
    setHintMessage(null);
  }, []);

  // Find all valid moves on the board
  const findAllValidMoves = useCallback(() => {
    if (!board || board.length === 0) return [];
    
    const validMoves = [];
    const size = board.length;

    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        if (board[row][col]?.isGap) continue;

        // Check right swap
        if (col < size - 1 && !board[row][col + 1]?.isGap) {
          const result = simulateSwap(board, row, col, row, col + 1, targetFruit);
          if (result.isValid) {
            validMoves.push({
              from: { row, col },
              to: { row, col: col + 1 },
              ...result
            });
          }
        }

        // Check down swap
        if (row < size - 1 && !board[row + 1][col]?.isGap) {
          const result = simulateSwap(board, row, col, row + 1, col, targetFruit);
          if (result.isValid) {
            validMoves.push({
              from: { row, col },
              to: { row: row + 1, col },
              ...result
            });
          }
        }
      }
    }

    return validMoves;
  }, [board, targetFruit]);

  // Simulate a swap and analyze the result
  const simulateSwap = useCallback((board, row1, col1, row2, col2, targetFruit) => {
    const testBoard = board.map(r => r.map(c => c ? { ...c } : null));
    [testBoard[row1][col1], testBoard[row2][col2]] = [testBoard[row2][col2], testBoard[row1][col1]];

    const matches = findMatchesSimple(testBoard);
    if (matches.length === 0) {
      return { isValid: false };
    }

    let targetFruitCount = 0;
    let matchLength = 0;
    let createsSpecial = false;
    let createsDiscoMelon = false;
    let matchTypes = new Set();

    matches.forEach(match => {
      matchLength = Math.max(matchLength, match.length);
      matchTypes.add(match.type);
      
      if (match.type === targetFruit) {
        targetFruitCount += match.length;
      }
      
      if (match.length === 4) {
        createsSpecial = true;
      }
      if (match.length >= 5) {
        createsDiscoMelon = true;
      }
    });

    return {
      isValid: true,
      targetFruitCount,
      matchLength,
      createsSpecial,
      createsDiscoMelon,
      matchTypes: Array.from(matchTypes),
    };
  }, []);

  // Simple match finder for simulation
  const findMatchesSimple = (board) => {
    const matches = [];
    const size = board.length;

    // Check horizontal matches
    for (let row = 0; row < size; row++) {
      let col = 0;
      while (col < size) {
        const cell = board[row][col];
        const fruit = cell?.type;
        if (!fruit || cell?.isGap) { col++; continue; }

        let matchLength = 1;
        while (col + matchLength < size && 
               board[row][col + matchLength]?.type === fruit &&
               !board[row][col + matchLength]?.isGap) {
          matchLength++;
        }

        if (matchLength >= 3) {
          matches.push({ type: fruit, length: matchLength, direction: 'horizontal' });
        }
        col += matchLength;
      }
    }

    // Check vertical matches
    for (let col = 0; col < size; col++) {
      let row = 0;
      while (row < size) {
        const cell = board[row][col];
        const fruit = cell?.type;
        if (!fruit || cell?.isGap) { row++; continue; }

        let matchLength = 1;
        while (row + matchLength < size && 
               board[row + matchLength][col]?.type === fruit &&
               !board[row + matchLength][col]?.isGap) {
          matchLength++;
        }

        if (matchLength >= 3) {
          matches.push({ type: fruit, length: matchLength, direction: 'vertical' });
        }
        row += matchLength;
      }
    }

    return matches;
  };

  // Get the best hint based on current game state
  const getBestHint = useCallback(() => {
    const validMoves = findAllValidMoves();
    if (validMoves.length === 0) return null;

    // Priority 1: Move that creates disco melon (5+ match)
    const discoMove = validMoves.find(m => m.createsDiscoMelon);
    if (discoMove) {
      return { move: discoMove, type: HINT_TYPES.COMBO, message: '🎉 Disco Melon! 5 in a row!' };
    }

    // Priority 2: Move that creates special fruit (4 match)
    const specialMove = validMoves.find(m => m.createsSpecial);
    if (specialMove) {
      return { move: specialMove, type: HINT_TYPES.SPECIAL, message: '⭐ Special fruit! 4 in a row!' };
    }

    // Priority 3: Move that collects target fruits
    const targetMoves = validMoves.filter(m => m.targetFruitCount > 0);
    if (targetMoves.length > 0) {
      const bestTargetMove = targetMoves.reduce((best, current) => 
        current.targetFruitCount > best.targetFruitCount ? current : best
      );
      return { 
        move: bestTargetMove, 
        type: HINT_TYPES.TARGETED, 
        message: `🎯 ${bestTargetMove.targetFruitCount}x target fruit!` 
      };
    }

    // Priority 4: Any valid move (longest match preferred)
    const sortedMoves = [...validMoves].sort((a, b) => b.matchLength - a.matchLength);
    return { move: sortedMoves[0], type: HINT_TYPES.BASIC, message: '💡 Valid move' };
  }, [findAllValidMoves]);

  // Show a hint
  const showHint = useCallback(() => {
    const hint = getBestHint();
    if (!hint) return false;

    const { move, type, message } = hint;
    setHintCells([move.from, move.to]);
    setHintType(type);
    setHintMessage(message);

    // Auto-clear hint after 3 seconds
    setTimeout(() => {
      clearHint();
    }, 3000);

    return true;
  }, [getBestHint, clearHint]);

  // Toggle auto-hint feature
  const toggleAutoHint = useCallback(() => {
    const newValue = !autoHintEnabled;
    setAutoHintEnabled(newValue);
    localStorage.setItem('bananaBlast_autoHint', newValue.toString());
  }, [autoHintEnabled]);

  // Auto-hint after idle time
  useEffect(() => {
    if (!autoHintEnabled) return;

    const interval = setInterval(() => {
      setIdleTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [autoHintEnabled]);

  useEffect(() => {
    if (autoHintEnabled && idleTime >= 10 && hintCells.length === 0) {
      showHint();
    }
  }, [autoHintEnabled, idleTime, hintCells.length, showHint]);

  // Reset idle time on board change
  useEffect(() => {
    setIdleTime(0);
    clearHint();
  }, [board, clearHint]);

  return {
    hintCells,
    hintType,
    hintMessage,
    showHint,
    clearHint,
    autoHintEnabled,
    toggleAutoHint,
    hasValidMoves: findAllValidMoves().length > 0,
  };
}

// Hint display component (floating tooltip)
export function HintTooltip({ message, type }) {
  if (!message) return null;

  const bgColors = {
    [HINT_TYPES.BASIC]: 'bg-blue-500',
    [HINT_TYPES.TARGETED]: 'bg-green-500',
    [HINT_TYPES.SPECIAL]: 'bg-purple-500',
    [HINT_TYPES.COMBO]: 'bg-amber-500',
  };

  return (
    <div className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-bounce-in`}>
      <div className={`${bgColors[type] || 'bg-blue-500'} text-white px-4 py-2 rounded-full shadow-lg font-display font-semibold`}>
        {message}
      </div>
    </div>
  );
}

// Hint button with options
export function HintButton({ onClick, hintsRemaining, disabled }) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      variant="outline"
      size="default"
      className="font-display font-semibold bg-gradient-to-r from-yellow-100 to-amber-100 hover:from-yellow-200 hover:to-amber-200 border-2 border-amber-400 text-amber-800 shadow-card"
      data-testid="hint-btn"
    >
      <Lightbulb className="w-4 h-4 mr-2" />
      Hint
      {hintsRemaining !== undefined && (
        <Badge variant="secondary" className="ml-2 bg-amber-200 text-amber-800">
          {hintsRemaining}
        </Badge>
      )}
    </Button>
  );
}

// Cell highlight component for hints
export function HintCellHighlight({ row, col, cellSize, boardOffset }) {
  const style = {
    position: 'absolute',
    left: `${boardOffset.x + col * cellSize}px`,
    top: `${boardOffset.y + row * cellSize}px`,
    width: `${cellSize}px`,
    height: `${cellSize}px`,
    pointerEvents: 'none',
    zIndex: 100,
  };

  return (
    <div style={style} className="hint-cell-highlight animate-pulse">
      <div className="absolute inset-1 rounded-xl border-4 border-yellow-400 bg-yellow-400/30 shadow-lg shadow-yellow-400/50">
        <div className="absolute inset-0 flex items-center justify-center">
          <Lightbulb className="w-6 h-6 text-yellow-600 animate-bounce" />
        </div>
      </div>
    </div>
  );
}

// Modal for hint explanation (tutorial-like)
export function HintExplanationModal({ isOpen, onClose, hintInfo }) {
  if (!hintInfo) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-amber-300">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl text-amber-800">
            <Lightbulb className="w-6 h-6 text-yellow-500" />
            Hint
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-white/70 rounded-xl border border-amber-200">
            <p className="font-display text-lg text-amber-900 text-center">
              {hintInfo.message}
            </p>
          </div>

          {hintInfo.explanation && (
            <div className="p-3 bg-amber-100/50 rounded-lg">
              <p className="text-sm text-amber-700">
                💡 {hintInfo.explanation}
              </p>
            </div>
          )}

          <div className="flex justify-center gap-2">
            {hintInfo.fruitTypes?.map((fruit, i) => (
              <span key={i} className="text-3xl">{FRUITS[fruit]?.emoji || '🍎'}</span>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button 
            onClick={onClose}
            className="font-display bg-amber-500 hover:bg-amber-600 text-white"
          >
            Begrepen!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
